/**
 * EnumCellEditorFooter — the action strip under `EnumCellEditor`'s value list.
 *
 * Three affordances, in escalating cost order:
 *
 *  1. **"Search rows in `dbo.Statuses`…"** — only when the column ALSO resolves
 *     to an FK target. Since a declared enum now outranks the FK for this cell
 *     (`cellEditVariant.ts`), this row is what keeps the full row picker
 *     reachable: the cached list is capped at 200 keys, and the picker is the
 *     way past that cap (and the way to search the target by any column).
 *  2. **Refresh values from data** — re-reads the value list (for a lookup
 *     column, the referenced table's key/label pairs).
 *  3. **Gear** — the enum column details dialog, only for declared columns.
 *
 * The picker dialog itself is NOT mounted here: it has to outlive the popover
 * hosting this footer, so the editor owns that state and this component only
 * reports the click (same split as `EnumPickerLookupFooter`).
 */

import { ExternalLink, Link2, RefreshCw, Settings2 } from 'lucide-react';
import type { CellEditFkTarget } from './cellEditVariant';
import { qualifyLookupTarget } from '../../utils/lookupEnumTarget';

export type EnumCellEditorFooterProps = {
  /** FK escape target — absent when the column has no reachable FK. */
  fkTarget: CellEditFkTarget | undefined;
  onSearchRows: () => void;
  onRefresh: () => void;
  refreshDisabled: boolean;
  refreshSpinning: boolean;
  /** Render the details gear (declared columns only). */
  showDetails: boolean;
  onOpenDetails: () => void;
};

export function EnumCellEditorFooter({
  fkTarget,
  onSearchRows,
  onRefresh,
  refreshDisabled,
  refreshSpinning,
  showDetails,
  onOpenDetails,
}: EnumCellEditorFooterProps) {
  return (
    <div className="border-t p-1">
      {fkTarget &&
        (() => {
          // Same FK icon convention as everywhere else (LazyForeignKeyCell,
          // FkCellEditor): teal Link2 for loose, blue ExternalLink for real.
          const Icon = fkTarget.isLoose ? Link2 : ExternalLink;
          const iconClass = fkTarget.isLoose
            ? 'h-3 w-3 mr-2 shrink-0 text-teal-600 dark:text-teal-400'
            : 'h-3 w-3 mr-2 shrink-0 text-blue-600 dark:text-blue-400';
          return (
            <button
              type="button"
              className="flex w-full items-center px-2 h-7 text-xs text-left rounded-sm hover:bg-accent"
              onPointerDown={(e) => {
                e.preventDefault();
                onSearchRows();
              }}
              data-testid="enum-cell-editor-search-row"
              data-loose={fkTarget.isLoose ? 'true' : 'false'}
            >
              <Icon className={iconClass} aria-hidden="true" />
              <span className="truncate">
                Search rows in{' '}
                <span translate="no" className="font-medium">
                  {qualifyLookupTarget(fkTarget.schema, fkTarget.table)}
                </span>
                …
              </span>
            </button>
          );
        })()}
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="flex-1 inline-flex items-center px-2 h-7 text-xs hover:bg-accent rounded-sm disabled:opacity-50"
          onPointerDown={(e) => {
            e.preventDefault();
            onRefresh();
          }}
          disabled={refreshDisabled}
          data-testid="enum-cell-editor-refresh"
        >
          <RefreshCw
            className={`h-3 w-3 mr-2 ${refreshSpinning ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
          Refresh values from data
        </button>
        {showDetails && (
          <button
            type="button"
            title="Open enum column details"
            aria-label="Open enum column details"
            onPointerDown={(e) => {
              e.preventDefault();
              onOpenDetails();
            }}
            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent shrink-0"
            data-testid="enum-cell-editor-settings"
          >
            <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
